import React, { ChangeEvent, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";

import "./Upload.scss";
import DropFiles from "./DropFiles";
import UploadTable from "./UploadTable";
import useThemeService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { uploadFilesService } from "../../services/upload/UploadFilesService";
import RadioButton from "../radio-button/RadioButton";
import { Dropdown, DropdownOption } from "../dropdown/Dropdown";
import {
  InspectionFileDataUpdateOption,
  InspectionRelevanceIntervalOption,
  InspectionRelevanceUpdateOption,
} from "../../generated/Inspections";

export default observer(function Upload() {
  const { themeClass } = useThemeService();

  const existFiles = useMemo(
    () => Boolean(uploadFilesService.filesForUpload.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadFilesService.filesForUpload.length]
  );

  const handleDataUpdateOptionChanged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      uploadFilesService.handleDataUpdateOptionChanged(
        e.target.value as InspectionFileDataUpdateOption
      ),
    []
  );

  const handleInspectionsRelevanceUpdateOptionChanged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      uploadFilesService.handleInspectionsRelevanceUpdateOptionChanged(
        e.target.value as InspectionRelevanceUpdateOption
      ),
    []
  );

  const handleInspectionsRelevanceIntervalChange = useCallback((newInterval: DropdownOption) => {
    uploadFilesService.handleInspectionsRelevanceIntervalChanged(
      newInterval.id as InspectionRelevanceIntervalOption
    );
  }, []);

  const x = Object.entries(InspectionRelevanceIntervalOption).map(([key, value]) => {
    return {
      id: value,
      name: translationService.translate(
        `${"general.upload.inspection-relevance-interval-"}${key.toLowerCase()}`
      ),
    };
  });

  return (
    <>
      <section className={themeClass(["upload-update-data-container"])}>
        <h4 className={themeClass(["upload-action-text"])}>
          {translationService.translate("general.upload.data-update.title")}
        </h4>
        <RadioButton
          name="file-data-option"
          labelKey="general.upload.data-update.new-and-existing-sewer-objects.option-label"
          onChange={handleDataUpdateOptionChanged}
          checked={
            uploadFilesService.fileDataUpdateOption ===
            InspectionFileDataUpdateOption.NewAndExistingObjects
          }
          value={String(InspectionFileDataUpdateOption.NewAndExistingObjects)}
        />
        <RadioButton
          name="file-data-option"
          labelKey="general.upload.data-update.new-sewer-objects.option-label"
          onChange={handleDataUpdateOptionChanged}
          checked={
            uploadFilesService.fileDataUpdateOption === InspectionFileDataUpdateOption.NewObjects
          }
          value={String(InspectionFileDataUpdateOption.NewObjects)}
        />
        <RadioButton
          name="file-data-option"
          labelKey="general.upload.data-update.geometries.option-label"
          onChange={handleDataUpdateOptionChanged}
          checked={
            uploadFilesService.fileDataUpdateOption ===
            InspectionFileDataUpdateOption.OnlyGeometries
          }
          value={String(InspectionFileDataUpdateOption.OnlyGeometries)}
        />
      </section>
      <section className={themeClass(["upload-update-data-container"])}>
        <div className="inspections-relevance-container">
          <RadioButton
            name="inspection-relevance-option"
            labelKey="general.upload.inspection-relevance-option-1"
            onChange={handleInspectionsRelevanceUpdateOptionChanged}
            checked={
              uploadFilesService.inspectionsRelevanceUpdateOption ===
              InspectionRelevanceUpdateOption.MarkOutdatedOlderThanInterval
            }
            value={String(InspectionRelevanceUpdateOption.MarkOutdatedOlderThanInterval)}
          />
          <div className="interval-container">
            {translationService.translate("general.upload.inspection-relevance-interval-label")}
            <Dropdown
              selectedOption={{ id: uploadFilesService.intervalOption, name: "" }}
              options={x}
              onChange={handleInspectionsRelevanceIntervalChange}
            />
          </div>
          <RadioButton
            name="inspection-relevance-option"
            labelKey="general.upload.inspection-relevance-option-2"
            onChange={handleInspectionsRelevanceUpdateOptionChanged}
            checked={
              uploadFilesService.inspectionsRelevanceUpdateOption ===
              InspectionRelevanceUpdateOption.KeepUnchanged
            }
            value={String(InspectionRelevanceUpdateOption.KeepUnchanged)}
          />
        </div>
      </section>
      <section className="upload-container">
        <h4 className={themeClass(["upload-action-text"])}>
          {translationService.translate("general.upload.title")}
        </h4>
        <DropFiles onDroppedFiles={uploadFilesService.addFilesForUpload} />
        <section>
          <h4 className={themeClass(["upload-result-text"])}>
            {translationService.translate("general.upload.added-files", {
              filesCount: uploadFilesService.filesForUpload.length,
            })}
          </h4>
          {uploadFilesService.noInspectionFileUploaded && (
            <div className="no-inspection-file-message">
              {translationService.translate("general.upload.no-inspection-file-uploaded")}
            </div>
          )}
          {existFiles && (
            <UploadTable
              files={uploadFilesService.filesForUpload}
              onRemoveFile={uploadFilesService.removeFile}
            />
          )}
        </section>
      </section>
    </>
  );
});
